import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getTemplateList, getTemplateUser } from "../../../../services/Templates";
import { toast } from "react-toastify";
import Utils from "../../../../utils";
import CreateFolder from "./CreateFolder";
import EditFolder from "./EditFolder";
import DeleteFolder from "./DeleteFolder";
import MoveFolder from "./MoveFolder";
import RenameEnvelope from "./RenameEnvelope";
import DeleteTemplateEnvelope from "./DeleteTemplateEnvelope";
import MoveEnvelope from "./MoveEnvelope";
import CopyTemplateEnvelope from "./CopyTemplateEnvelope";
import ShareFolder from "./ShareFolder";
import RemoveShareFolder from "./RemoveShareFolder";
import DuplicateTemplate from "./DuplicateTemplate";
import '../../../../assets/template-ui.css';
// import { ReactComponent as FolderIcon } from '../../../../assets/svg/folder.svg';
import { ReactComponent as CalendarIcon } from '../../../../assets/svg/calendar.svg';
import DropdownTriggerIcon from './DropdownTriggerIcon';

// Custom Template Icon SVG
const TemplateIcon = (props) => (
  <svg
    width={32}
    height={32}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#a259c6"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <line x1="8" y1="6" x2="16" y2="6" />
    <line x1="8" y1="10" x2="16" y2="10" />
    <line x1="8" y1="14" x2="12" y2="14" />
  </svg>
);

function Templates() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState('grid');
  const buttonList = [
    { id: 1, name: 'My Templates', active: true },
    { id: 2, name: 'Shared with me', active: false },
    { id: 3, name: 'Templates', active: false }
  ];
  const [buttons, setButtons] = useState(buttonList);
  const [isBack, setIsBack] = useState(-1);
  const [templateList, setTemplateList] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [uuid, setUuid] = useState("");
  const [shareUserList, setShareUserList] = useState([]);

  const currentTemplateObj = {
    tab_id: 1,
    current_tab: "My Templates",
    current_folder: 0,
    is_refresh: false
  };
  const [currentTemplate, setCurrentTemplate] = useState(currentTemplateObj);

  // Ensure buttons state is synchronized with currentTemplate on mount
  useEffect(() => {
    const updatedButtons = buttonList.map(button => ({
      ...button,
      active: button.id === currentTemplate.tab_id
    }));
    setButtons(updatedButtons);
  }, []); // Only run on mount
  const [isRefresh, setIsRefresh] = useState(false);

  const [folderData, setFolderData] = useState({
    id: '',
    name: ''
  });

  const [removeShareData, setRemoveShareData] = useState({
    id: '',
    type: ''
  });

  // Add state for create dropdown open/close
  const [isCreateDropdownOpen, setIsCreateDropdownOpen] = useState(false);

  // Function to highlight the search term in the text
  const highlightSearchTerm = (text, search) => {
    if (!search || !text) return text;
    const regex = new RegExp(`(${search.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="highlight-search">{part}</span>
      ) : (
        part
      )
    );
  };

  const fetchTemplateList = (obj, useLoader = true) => {
    if (useLoader) {
      setLoading(true);
    }
    getTemplateList(obj)
      .then(response => {
        setTemplateList(response.data.data);
        setBreadcrumbs(response.data.breadcrumbs);
        setIsBack(response.data.back_id);
        setUuid(response.data.uuid);
        if (useLoader) {
          setLoading(false);
        }
      })
      .catch(err => {
        if (useLoader) {
          setLoading(false);
        }
        toast.error(Utils.getErrorMessage(err));
      });
  };

  // Handle navigation state for folder navigation
  useEffect(function () {
    if (location.state && location.state.folderId) {
      try {
        // Set the current folder to the one specified in navigation state
        let currentTem = { ...currentTemplate };
        currentTem.current_folder = parseInt(location.state.folderId);

        // Preserve the original tab if specified, otherwise use current tab
        if (location.state.originalTabId) {
          currentTem.tab_id = location.state.originalTabId;
          currentTem.current_tab = buttonList.find(b => b.id === location.state.originalTabId)?.name || "My Templates";

          // Also preserve the folder if specified
          if (location.state.returnFolderId !== undefined) {
            currentTem.current_folder = parseInt(location.state.returnFolderId);
          }
        } else if (location.state.fromModifyTemplate || location.state.fromViewTemplate) {
          // Fallback: If coming from ModifyTemplate or ViewTemplate without original tab, use Templates tab
          currentTem.tab_id = 3;
          currentTem.current_tab = "Templates";
        }

        // Update the buttons state to reflect the correct tab as active
        let updatedButtons = [...buttonList];
        updatedButtons.forEach(button => {
          button.active = button.id === currentTem.tab_id;
        });
        setButtons(updatedButtons);

        setCurrentTemplate(currentTem);

        // Show a toast message if coming from ModifyTemplate or ViewTemplate
        if (location.state.fromModifyTemplate) {
          toast.success('Navigated to selected folder');
        } else if (location.state.fromViewTemplate) {
          toast.success('Navigated to selected folder');
        }



        // Don't clear navigation state automatically - let it be cleared naturally
        // This ensures the state updates are properly processed
      } catch (error) {
        console.error('Error setting folder from navigation state:', error);
        // Don't clear navigation state on error - let it be processed naturally
      }
    }
  }, [location.state]);

  // Handle URL parameters for folder navigation (fallback)
  useEffect(function () {
    const searchParams = new URLSearchParams(location.search);
    const folderId = searchParams.get('folder');
    const originalTabId = searchParams.get('tab');

    if (folderId) {
      try {
        // Set the current folder to the one specified in URL parameter
        let currentTem = { ...currentTemplate };
        currentTem.current_folder = parseInt(folderId);

        // Preserve the original tab if specified in URL, otherwise use current tab
        if (originalTabId) {
          currentTem.tab_id = parseInt(originalTabId);
          currentTem.current_tab = buttonList.find(b => b.id === parseInt(originalTabId))?.name || "My Templates";
        } else {
          // For URL parameter fallback without tab info, use Templates tab (tab_id: 3)
          // since this is typically used when coming from ModifyTemplate or ViewTemplate
          currentTem.tab_id = 3;
          currentTem.current_tab = "Templates";
        }

        // Update the buttons state to reflect the correct tab as active
        let updatedButtons = [...buttonList];
        updatedButtons.forEach(button => {
          button.active = button.id === currentTem.tab_id;
        });
        setButtons(updatedButtons);

        setCurrentTemplate(currentTem);

        // Clear the URL parameter after setting the folder
        navigate('/templates', { replace: true });
      } catch (error) {
        console.error('Error setting folder from URL parameter:', error);
        navigate('/templates', { replace: true });
      }
    }
  }, [location.search]);

  useEffect(function () {
    getTemplateUser({})
      .then(response => {
        setShareUserList(response.data.data);
      })
      .catch(err => {
        console.error(err);
      });
  }, []);

  useEffect(function () {
    let obj = {
      template: currentTemplate.tab_id,
      folder_id: currentTemplate.current_folder,
      search_term: searchTerm
    };
    fetchTemplateList(obj, true);
  }, [currentTemplate, isRefresh]);

  useEffect(function () {
    let obj = {
      template: currentTemplate.tab_id,
      folder_id: currentTemplate.current_folder,
      search_term: searchTerm
    };
    fetchTemplateList(obj, false);
  }, [searchTerm]);

  const handleTemplateButton = (e, data) => {
    e.preventDefault();
    let button = [...buttonList];
    let currentTem = { ...currentTemplate };
    for (let i = 0; i < button.length; i++) {
      let active = false;
      if (button[i]['id'] === data.id) {
        active = true;
        currentTem.tab_id = data.id;
        currentTem.current_tab = button[i]['name'];
        // Reset to root folder when switching tabs
        currentTem.current_folder = 0;
        setSearchTerm("");
      }
      button[i]['active'] = active;
    }
    setButtons(button);
    setCurrentTemplate(currentTem);
  };

  const handleBack = () => {
    if (breadcrumbs.length > 0) {
      // Go to parent folder (second last breadcrumb)
      if (breadcrumbs.length === 1) {
        // Only one breadcrumb, go to root
        let currentTem = { ...currentTemplate };
        currentTem.current_folder = 0;
        setCurrentTemplate(currentTem);
      } else {
        // Go to parent
        let parentId = breadcrumbs[breadcrumbs.length - 2].id;
        let currentTem = { ...currentTemplate };
        currentTem.current_folder = parentId;
        setCurrentTemplate(currentTem);
      }
    } else {
      // Already at root, do nothing or reset
      let currentTem = { ...currentTemplate };
      currentTem.current_folder = 0;
      setCurrentTemplate(currentTem);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const onCreateTemplate = (e) => {
    e.preventDefault();
    navigate('/templates/create/' + uuid, {
      state: {
        returnTabId: currentTemplate.tab_id,
        returnTabName: currentTemplate.current_tab,
        returnFolderId: currentTemplate.current_folder,
        returnBreadcrumbs: breadcrumbs
      }
    });
  };

  const onClickFolder = (e, id) => {
    e.preventDefault();
    let currentTem = { ...currentTemplate };
    currentTem.current_folder = parseInt(id);
    setSearchTerm("");
    setCurrentTemplate(currentTem);
  };

  const onOpenMoveFolder = (e, data) => {
    e.preventDefault();
    let obj = {
      id: data.entity_id,
      name: data.template_name
    };
    setFolderData(obj);
  };

  const onOpenShareFolder = (e, data) => {
    e.preventDefault();
    let obj = {
      id: data.entity_id,
      type: data.entity_type
    };
    setRemoveShareData(obj);
  };

  const onOpenEditFolder = (e, data) => {
    e.preventDefault();
    let obj = {
      id: data.entity_id,
      name: data.template_name
    };
    setFolderData(obj);
  };

  const onOpenDeleteFolder = (e, data) => {
    e.preventDefault();
    let obj = {
      id: data.entity_id,
      name: data.template_name
    };
    setFolderData(obj);
  };

  const onEditTemplate = (e, data) => {
    e.preventDefault();
    navigate('/templates/' + data.template_uuid + '/edit', {
      state: {
        returnTabId: currentTemplate.tab_id,
        returnTabName: currentTemplate.current_tab,
        returnFolderId: currentTemplate.current_folder,
        returnBreadcrumbs: breadcrumbs
      }
    });
  };

  const onCopyTemplate = (e, data) => {
    e.preventDefault();
    let obj = {
      id: data.entity_id,
      name: data.template_name
    };
    setFolderData(obj);
  };

  const onOpenMoveTemplate = (e, data) => {
    e.preventDefault();
    let obj = {
      id: data.entity_id,
      name: data.template_name
    };
    setFolderData(obj);
  };

  const onOpenRenameTemplate = (e, data) => {
    e.preventDefault();
    let obj = {
      id: data.entity_id,
      name: data.template_name
    };
    setFolderData(obj);
  };

  const onOpenDeleteTemplate = (e, data) => {
    e.preventDefault();
    let obj = {
      id: data.entity_id,
      name: data.template_name
    };
    setFolderData(obj);
  };

  const onOpenRemoveShareFolder = (e, data) => {
    setRemoveShareData({
      id: data.entity_id,
      type: data.entity_type
    });
  };

  const onViewTemplate = (e, data) => {
    e.preventDefault();
    navigate('/templates/' + data.template_uuid + '/view', {
      state: {
        returnTabId: currentTemplate.tab_id,
        returnTabName: currentTemplate.current_tab,
        returnFolderId: currentTemplate.current_folder,
        returnBreadcrumbs: breadcrumbs
      }
    });
  };

  return (
    <>
      {loading && (
        <div className="page-loading">
          <img src="/images/loader.gif" alt="loader" />
        </div>
      )}
      <div
        className="section-bg-color wrapper-sleeve sleeve-spacing res-height">
        <div className="custom_container">
          <div className="modern-nav-tabs-container">
            <div className="modern-nav-tabs">
              {buttons.map((item, idx) => {
                // Choose icon based on button name
                let iconClass = "bi bi-folder-fill";
                if (item.name === "My Templates")
                  iconClass = "bi bi-folder-fill";
                else if (item.name === "Shared with me")
                  iconClass = "bi bi-people-fill";
                else if (item.name === "Templates") iconClass = "bi bi-stars";
                return (
                  <button
                    key={idx}
                    className={`modern-nav-tab ${item.active ? "active" : ""}`}
                    onClick={(e) => handleTemplateButton(e, item)}
                    type="button"
                  >
                    <div className="modern-nav-tab-icon">
                      <i className={iconClass} />
                    </div>
                    <span className="modern-nav-tab-label">{item.name}</span>
                    {item.active && <div className="modern-nav-tab-indicator"></div>}
                  </button>
                );
              })}
            </div>
          </div>

          <main className="template-ui-content">
            {/* Clean Toolbar */}
            <div className="template-ui-toolbar-row">
              <div className="template-ui-search-group">
                <input
                  className="template-ui-search-input"
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="template-ui-toolbar-actions">
                <div className="template-ui-view-toggle-group">
                  <button
                    className={`template-ui-view-toggle-btn${viewMode === "grid" ? " active" : ""}`}
                    onClick={() => setViewMode("grid")}
                    title="Grid View"
                  >
                    <i className="bi bi-grid" />
                  </button>
                  <button
                    className={`template-ui-view-toggle-btn${viewMode === "table" ? " active" : ""}`}
                    onClick={() => setViewMode("table")}
                    title="List View"
                  >
                    <i className="bi bi-list" />
                  </button>
                </div>

                {isBack !== -1 && (
                  <button
                    className="modern-back-btn"
                    onClick={handleBack}
                  >
                    <i className="bi bi-arrow-left"></i>
                    <span>Back</span>
                  </button>
                )}

                {currentTemplate.tab_id === 1 && (
                  <div className="dropdown">
                    <button
                      className="template-ui-create-btn dropdown-toggle"
                      type="button"
                      id="createDropdown"
                      data-bs-toggle="dropdown"
                      aria-expanded={isCreateDropdownOpen}
                      onClick={() => setIsCreateDropdownOpen((open) => !open)}
                      onBlur={() => setIsCreateDropdownOpen(false)}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Create
                      <i className={`ms-2 bi ${isCreateDropdownOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    </button>
                    <ul className="dropdown-menu" aria-labelledby="createDropdown">
                      <li>
                        <button className="dropdown-item" type="button" onClick={onCreateTemplate}>
                          <i className="bi bi-file-earmark-plus me-2"></i>
                          Create Template
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item" type="button" data-bs-toggle="modal" data-bs-target="#createTemplateFolder">
                          <i className="bi bi-folder-plus me-2"></i>
                          Create Folder
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Template List Section */}
            <div className="template-ui-list-section">
              <div className="teplate-folder">
                {viewMode === "table" ? (
                  <div className="template-ui-template-list">
                    {/* Clean Breadcrumbs */}
                    <div className="template-table-breadcrumbs">
                      <span
                        className="breadcrumb-link"
                        onClick={e => handleTemplateButton(e, buttons.find(b => b.name === currentTemplate.current_tab) || buttons[0])}
                      >
                        {currentTemplate.current_tab}
                      </span>
                      {breadcrumbs.map((breadcrumb, b) => (
                        <React.Fragment key={b}>
                          <span className="mx-2 text-muted">/</span>
                          {b === breadcrumbs.length - 1 ? (
                            <span className="text-dark">{breadcrumb.folder_name}</span>
                          ) : (
                            <span
                              className="breadcrumb-link"
                              onClick={e => onClickFolder(e, breadcrumb.id)}
                            >
                              {breadcrumb.folder_name}
                            </span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>

                    {/* Table Body */}
                    <div className="template-table-body">
                      {templateList.map((item, index) => (
                        <div className="template-table-row" key={index}>
                          <div className="template-table-cell template-table-cell-name">
                            <div className="template-table-item-info">
                              <div className="template-table-item-icon">
                                {item.entity_type === 1 ? (
                                  <div className="template-folder-icon">
                                    <i className="bi bi-folder" />
                                  </div>
                                ) : (
                                  <div className="template-file-icon">
                                    <TemplateIcon />
                                  </div>
                                )}
                              </div>
                              <div className="template-table-item-details">
                                <div className="template-table-item-name">
                                  {item.entity_type === 1 ? (
                                    <span
                                      onClick={(e) => onClickFolder(e, item.entity_id)}
                                      className="template-folder-name cur-pointer"
                                      title="Open Folder"
                                    >
                                      {highlightSearchTerm(item.template_name, searchTerm)}
                                    </span>
                                  ) : (
                                    <span
                                      onClick={(e) => onViewTemplate(e, item)}
                                      className="template-file-name cur-pointer"
                                      title="View Template"
                                    >
                                      {highlightSearchTerm(item.template_name, searchTerm)}
                                    </span>
                                  )}
                                </div>
                                <div className="template-table-item-type">
                                  {item.entity_type === 1 ? 'Folder' : 'Template'}
                                </div>
                              </div>
                            </div>
                          </div>

                          {currentTemplate.tab_id === 1 && (
                            <>
                              <div className="template-table-cell template-table-cell-status">
                                {item.is_share === 0 ? (
                                  <span className="status-badge status-not-shared">
                                    <i className="bi bi-lock"></i>
                                    Not Shared
                                  </span>
                                ) : (
                                  <span className="status-badge status-shared">
                                    <i className="bi bi-share me-1"></i>
                                    Shared
                                  </span>
                                )}
                              </div>
                              <div className="template-table-cell template-table-cell-date d-flex justify-content-end">
                                <div className="date-info">
                                  <CalendarIcon className="date-icon" />
                                  <span className="date-text">{item.updated_at}</span>
                                </div>
                              </div>
                            </>
                          )}

                          {currentTemplate.tab_id === 2 && (
                            <>
                              <div className="template-table-cell template-table-cell-sender">
                                <div className="sender-info">
                                  <div className="sender-avatar">
                                    <i className="bi bi-person-circle"></i>
                                  </div>
                                  <span className="sender-name">{item.sender_name}</span>
                                </div>
                              </div>
                              <div className="template-table-cell template-table-cell-date">
                                <div className="date-info">
                                  <CalendarIcon className="date-icon" />
                                  <span className="date-text"><CalendarIcon className="me-1" />{item.created_at}</span>
                                </div>
                              </div>
                            </>
                          )}

                          {currentTemplate.tab_id === 3 && (
                            <div className="template-table-cell template-table-cell-date">
                              <div className="date-info">
                                <CalendarIcon className="date-icon" />
                                <span className="date-text"><CalendarIcon className="me-1" /> {item.created_at}</span>
                              </div>
                            </div>
                          )}

                          {currentTemplate.tab_id === 3 && (
                            <div className="template-table-cell template-table-cell-date">

                            </div>
                          )}


                          <div className="template-table-cell template-table-cell-actions">
                            <div className="template-table-actions">
                              {currentTemplate.tab_id === 1 && (
                                <>
                                  <div className="dropdown">
                                    <button
                                      className="template-action-btn"
                                      type="button"
                                      id={`dropdownMenuButton-${index}`}
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <i className="bi bi-three-dots-vertical"></i>
                                    </button>
                                    <ul
                                      className="dropdown-menu template-dropdown-menu"
                                      aria-labelledby={`dropdownMenuButton-${index}`}
                                    >
                                      {item.entity_type === 1 ? (
                                        <>
                                          <li>
                                            <button
                                              className="dropdown-item"
                                              onClick={(e) => onOpenShareFolder(e, item)}
                                              data-bs-toggle="modal"
                                              data-bs-target="#shareFolder"
                                            >
                                              <i className="bi bi-share-fill me-2"></i> Share Folder
                                            </button>
                                          </li>
                                          <li>
                                            <button
                                              className="dropdown-item"
                                              onClick={(e) => onOpenMoveFolder(e, item)}
                                              data-bs-toggle="modal"
                                              data-bs-target="#moveTemplateFolder"
                                            >
                                              <i className="bi bi-arrows-move me-2"></i> Move Folder
                                            </button>
                                          </li>
                                          <li>
                                            <button
                                              className="dropdown-item"
                                              onClick={(e) => onOpenEditFolder(e, item)}
                                              data-bs-toggle="modal"
                                              data-bs-target="#editTemplateFolder"
                                            >
                                              <i className="bi bi-type me-2"></i> Rename Folder
                                            </button>
                                          </li>
                                          <li>
                                            <button
                                              className="dropdown-item text-danger"
                                              onClick={(e) => onOpenDeleteFolder(e, item)}
                                              data-bs-toggle="modal"
                                              data-bs-target="#deleteTemplateFolder"
                                            >
                                              <i className="bi bi-trash me-2"></i> Delete Folder
                                            </button>
                                          </li>
                                        </>
                                      ) : (
                                        <>
                                          <li>
                                            <button
                                              className="dropdown-item"
                                              onClick={(e) => onEditTemplate(e, item)}
                                            >
                                              <i className="bi bi-pencil me-2"></i> Edit Template
                                            </button>
                                          </li>
                                          <li>
                                            <button
                                              className="dropdown-item"
                                              onClick={(e) => onCopyTemplate(e, item)}
                                              data-bs-toggle="modal"
                                              data-bs-target="#copyTemplateEnvelope"
                                            >
                                              <i className="bi bi-files me-2"></i> Copy Template
                                            </button>
                                          </li>
                                          <li>
                                            <button
                                              className="dropdown-item"
                                              onClick={(e) => onOpenShareFolder(e, item)}
                                              data-bs-toggle="modal"
                                              data-bs-target="#shareFolder"
                                            >
                                              <i className="bi bi-share me-2"></i> Share Template
                                            </button>
                                          </li>
                                          <li>
                                            <button
                                              className="dropdown-item"
                                              onClick={(e) => onOpenMoveTemplate(e, item)}
                                              data-bs-toggle="modal"
                                              data-bs-target="#moveTemplate"
                                            >
                                              <i className="bi bi-arrows-move me-2"></i> Move Template
                                            </button>
                                          </li>
                                          <li>
                                            <button
                                              className="dropdown-item"
                                              onClick={(e) => onOpenRenameTemplate(e, item)}
                                              data-bs-toggle="modal"
                                              data-bs-target="#renameTemplate"
                                            >
                                              <i className="bi bi-type me-2"></i> Rename Template
                                            </button>
                                          </li>
                                          <li>
                                            <button
                                              className="dropdown-item text-danger"
                                              onClick={(e) => onOpenDeleteTemplate(e, item)}
                                              data-bs-toggle="modal"
                                              data-bs-target="#deleteEnvelopeTemplate"
                                            >
                                              <i className="bi bi-trash me-2"></i> Delete Template
                                            </button>
                                          </li>
                                        </>
                                      )}
                                    </ul>
                                  </div>
                                </>
                              )}

                              {currentTemplate.tab_id === 2 && (
                                <div className="dropdown">
                                  <button
                                    className="template-action-btn"
                                    type="button"
                                    id={`dropdownMenuButton-${index}`}
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <i className="bi bi-three-dots-vertical"></i>
                                  </button>
                                  <ul
                                    className="dropdown-menu template-dropdown-menu"
                                    aria-labelledby={`dropdownMenuButton-${index}`}
                                  >
                                    {item.entity_type === 1 ? (
                                      <li>
                                        <button
                                          className="dropdown-item text-danger"
                                          onClick={(e) => onOpenRemoveShareFolder(e, item)}
                                          data-bs-toggle="modal"
                                          data-bs-target="#removeShareFolder"
                                        >
                                          <i className="bi bi-x-circle me-2"></i> Remove from Shared
                                        </button>
                                      </li>
                                    ) : (
                                      <>
                                        <li>
                                          <button
                                            className="dropdown-item"
                                            onClick={(e) => onViewTemplate(e, item)}
                                          >
                                            <i className="bi bi-eye me-2"></i> View Template
                                          </button>
                                        </li>
                                        <li>
                                          <button
                                            className="dropdown-item"
                                            onClick={(e) => onCopyTemplate(e, item)}
                                            data-bs-toggle="modal"
                                            data-bs-target="#duplicateTemplateModal"
                                          >
                                            <i className="bi bi-files me-2"></i> Duplicate Template
                                          </button>
                                        </li>
                                        <li>
                                          <button
                                            className="dropdown-item text-danger"
                                            onClick={(e) => onOpenRemoveShareFolder(e, item)}
                                            data-bs-toggle="modal"
                                            data-bs-target="#removeShareFolder"
                                          >
                                            <i className="bi bi-x-circle me-2"></i> Remove from Shared
                                          </button>
                                        </li>
                                      </>
                                    )}
                                  </ul>
                                </div>
                              )}

                              {currentTemplate.tab_id === 3 && (
                                <>
                                  <div>

                                  </div>
                                  <div className="dropdown">
                                    <button
                                      className="template-action-btn"
                                      type="button"
                                      id={`dropdownMenuButton-${item.entity_id}`}
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <i className="bi bi-three-dots-vertical"></i>
                                    </button>
                                    <ul
                                      className="dropdown-menu template-dropdown-menu"
                                      aria-labelledby={`dropdownMenuButton-${item.entity_id}`}
                                    >
                                      {item.entity_type === 1 ? (
                                        <li>
                                          <button
                                            className="dropdown-item"
                                            onClick={(e) => onClickFolder(e, item.entity_id)}
                                          >
                                            <i className="bi bi-folder2-open me-2"></i> Open Folder
                                          </button>
                                        </li>
                                      ) : (
                                        <>
                                          <li>
                                            <button
                                              className="dropdown-item"
                                              onClick={(e) => onViewTemplate(e, item)}
                                            >
                                              <i className="bi bi-eye me-2"></i> View Template
                                            </button>
                                          </li>
                                          <li>
                                            <button
                                              className="dropdown-item"
                                              onClick={(e) => onCopyTemplate(e, item)}
                                              data-bs-toggle="modal"
                                              data-bs-target="#duplicateTemplateModal"
                                            >
                                              <i className="bi bi-files me-2"></i> Copy Template
                                            </button>
                                          </li>
                                        </>
                                      )}
                                    </ul>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                ) : (
                  <>
                    {/* Clean Grid View */}
                    <div className="template-table-breadcrumbs mb-3">
                      <span
                        className="breadcrumb-link"
                        onClick={e => handleTemplateButton(e, buttons.find(b => b.name === currentTemplate.current_tab) || buttons[0])}
                      >
                        {currentTemplate.current_tab}
                      </span>
                      {breadcrumbs.map((breadcrumb, b) => (
                        <React.Fragment key={b}>
                          <span className="mx-2 text-muted">/</span>
                          {b === breadcrumbs.length - 1 ? (
                            <span className="text-dark">{breadcrumb.folder_name}</span>
                          ) : (
                            <span
                              className="breadcrumb-link"
                              onClick={e => onClickFolder(e, breadcrumb.id)}
                            >
                              {breadcrumb.folder_name}
                            </span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>

                    {templateList.length > 0 ? (
                      <div className="template-ui-folder-grid">
                        {templateList.map((item, index) => (
                          <div
                            className="custom-folder-view"
                            key={index}
                            onClick={(e) => {
                              // Open folder/template when clicking anywhere on the card
                              if (item.entity_type === 1) {
                                onClickFolder(e, item.entity_id);
                              } else {
                                onViewTemplate(e, item);
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {/* Header - Icon and Three dots inline */}
                            <div className="template-header">
                              <div className="template-icon">
                                {item.entity_type === 1 ? (
                                  <div className="template-folder-icon">
                                    <i className="bi bi-folder" />
                                  </div>
                                ) : (
                                  <div className="template-file-icon">
                                    <TemplateIcon />
                                  </div>
                                )}
                              </div>

                              <div className="template-actions">
                                {currentTemplate.tab_id === 1 && (
                                  <div className="dropdown">
                                    <span
                                      className="functional_icon_ellipsis_template"
                                      id={`dropdownMenuButton-${index}`}
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                      style={{ cursor: "pointer" }}
                                      onClick={e => {
                                        e.stopPropagation(); // Prevent card click when clicking three dots
                                      }}
                                    >
                                      <DropdownTriggerIcon />
                                    </span>
                                    <ul
                                      className="dropdown-menu z-3 shadow-sm border-0 rounded-3 py-2"
                                      aria-labelledby={`dropdownMenuButton-${index}`}
                                    >
                                      {item.entity_type === 1 ? (
                                        <>
                                          <li
                                            data-bs-toggle="modal"
                                            onClick={(e) => { e.stopPropagation(); onOpenShareFolder(e, item) }}
                                            data-bs-target="#shareFolder"
                                          >
                                            <span className="dropdown-item">
                                              <i className="bi bi-share-fill me-2"></i> Share Folder
                                            </span>
                                          </li>
                                          <li
                                            data-bs-toggle="modal"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenMoveFolder(e, item)
                                            }}
                                            data-bs-target="#moveTemplateFolder"
                                          >
                                            <span className="dropdown-item">
                                              <i className="bi bi-arrows-move me-2"></i> Move Folder
                                            </span>
                                          </li>
                                          <li
                                            data-bs-toggle="modal"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenEditFolder(e, item)
                                            }}
                                            data-bs-target="#editTemplateFolder"
                                          >
                                            <span className="dropdown-item">
                                              <i className="bi bi-type me-2"></i> Rename Folder
                                            </span>
                                          </li>
                                          <li
                                            data-bs-toggle="modal"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenDeleteFolder(e, item)
                                            }}
                                            data-bs-target="#deleteTemplateFolder"
                                          >
                                            <span className="dropdown-item text-danger">
                                              <i className="bi bi-trash me-2"></i> Delete Folder
                                            </span>
                                          </li>
                                        </>
                                      ) : (
                                        <>
                                          <li
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onEditTemplate(e, item)
                                            }}
                                          >
                                            <span className="dropdown-item">
                                              <i className="bi bi-pencil me-2"></i> Edit Template
                                            </span>
                                          </li>
                                          <li
                                            data-bs-toggle="modal"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onCopyTemplate(e, item)
                                            }}
                                            data-bs-target="#copyTemplateEnvelope"
                                          >
                                            <span className="dropdown-item">
                                              <i className="bi bi-files me-2"></i> Copy Template
                                            </span>
                                          </li>
                                          <li
                                            data-bs-toggle="modal"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenShareFolder(e, item)
                                            }}
                                            data-bs-target="#shareFolder"
                                          >
                                            <span className="dropdown-item">
                                              <i className="bi bi-share me-2"></i> Share Template
                                            </span>
                                          </li>
                                          <li
                                            data-bs-toggle="modal"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenMoveTemplate(e, item)
                                            }}
                                            data-bs-target="#moveTemplate"
                                          >
                                            <span className="dropdown-item">
                                              <i className="bi bi-arrows-move me-2"></i> Move Template
                                            </span>
                                          </li>
                                          <li
                                            data-bs-toggle="modal"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenRenameTemplate(e, item)
                                            }}
                                            data-bs-target="#renameTemplate"
                                          >
                                            <span className="dropdown-item">
                                              <i className="bi bi-type me-2"></i> Rename Template
                                            </span>
                                          </li>
                                          <li
                                            data-bs-toggle="modal"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenDeleteTemplate(e, item)
                                            }}
                                            data-bs-target="#deleteEnvelopeTemplate"
                                          >
                                            <span className="dropdown-item text-danger">
                                              <i className="bi bi-trash me-2"></i> Delete Template
                                            </span>
                                          </li>
                                        </>
                                      )}
                                    </ul>
                                  </div>
                                )}
                                {currentTemplate.tab_id === 2 && (
                                  <div className="dropdown">
                                    <span
                                      className="functional_icon_ellipsis_template"
                                      id={`dropdownMenuButton-${index}`}
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                      style={{ cursor: "pointer" }}
                                      onClick={e => {
                                        e.stopPropagation(); // Prevent card click when clicking three dots
                                      }}
                                    >
                                      <DropdownTriggerIcon />
                                    </span>
                                    <ul
                                      className="dropdown-menu z-3 shadow-sm border-0 rounded-3 py-2"
                                      aria-labelledby={`dropdownMenuButton-${index}`}
                                    >
                                      {item.entity_type === 1 ? (
                                        <li
                                          data-bs-toggle="modal"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onOpenRemoveShareFolder(e, item)
                                          }}
                                          data-bs-target="#removeShareFolder"
                                        >
                                          <span className="dropdown-item text-danger">
                                            <i className="bi bi-x-circle me-2"></i> Remove from Shared with me
                                          </span>
                                        </li>
                                      ) : (
                                        <>
                                          <li
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onViewTemplate(e, item)
                                            }}
                                          >
                                            <span className="dropdown-item">
                                              <i className="bi bi-eye me-2"></i> View Template
                                            </span>
                                          </li>
                                          <li
                                            data-bs-toggle="modal"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onCopyTemplate(e, item)
                                            }}
                                            data-bs-target="#duplicateTemplateModal"
                                          >
                                            <span className="dropdown-item">
                                              <i className="bi bi-files me-2"></i> Duplicate template
                                            </span>
                                          </li>
                                          <li
                                            data-bs-toggle="modal"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenRemoveShareFolder(e, item)
                                            }}
                                            data-bs-target="#removeShareFolder"
                                          >
                                            <span className="dropdown-item text-danger">
                                              <i className="bi bi-x-circle me-2"></i> Remove from Shared with me
                                            </span>
                                          </li>
                                        </>
                                      )}
                                    </ul>
                                  </div>
                                )}
                                {currentTemplate.tab_id === 3 &&
                                  (item.entity_type === 1 ? (
                                    <div className="dropdown">
                                      {/* Folder actions for tab 3 */}
                                    </div>
                                  ) : (
                                    <div className="dropdown">
                                      <span
                                        className="functional_icon_ellipsis_template_folder"
                                        id={`dropdownMenuButton-${item.entity_id}`}
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                        style={{ cursor: "pointer" }}
                                        onClick={e => {
                                          e.stopPropagation(); // Prevent card click when clicking three dots
                                        }}
                                      >
                                        <DropdownTriggerIcon />
                                      </span>
                                      <ul
                                        className="dropdown-menu z-3 shadow-sm border-0 rounded-3 py-2"
                                        aria-labelledby={`dropdownMenuButton-${item.entity_id}`}
                                      >
                                        <li
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onViewTemplate(e, item)
                                          }}
                                        >
                                          <span className="dropdown-item">
                                            <i className="bi bi-eye me-2"></i> View Template
                                          </span>
                                        </li>
                                        <li
                                          data-bs-toggle="modal"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onCopyTemplate(e, item)
                                          }}
                                          data-bs-target="#duplicateTemplateModal"
                                        >
                                          <span className="dropdown-item">
                                            <i className="bi bi-files me-2"></i> Copy Template
                                          </span>
                                        </li>
                                      </ul>
                                    </div>
                                  ))}
                              </div>
                            </div>

                            {/* Template/Folder Name */}
                            <div className="template-name-section">
                              <span
                                className={`template-name ${item.entity_type === 1 ? 'template-folder-name' : 'template-file-name'
                                  }`}
                                title={item.entity_type === 1 ? "Open Folder" : "View Template"}
                              >
                                {highlightSearchTerm(item.template_name, searchTerm)}
                              </span>
                            </div>

                            {/* Footer - Status and Date */}
                            {currentTemplate.tab_id === 1 && (
                              <div className="template-footer">
                                <div className="template-status">
                                  {item.is_share === 0 ? (
                                    <span className="badge-template bg-dark-subtle text-dark-emphasis">
                                      Not Shared
                                    </span>
                                  ) : (
                                    <span className="badge-template bg-success bg-opacity-10 text-success">
                                      Shared
                                    </span>
                                  )}
                                </div>
                                <div className="template-date">
                                  <CalendarIcon />
                                  <span>{item.updated_at}</span>
                                </div>
                              </div>
                            )}
                            {currentTemplate.tab_id === 2 && (
                              <div className="template-footer">
                                <div className="template-sender">
                                  <span className="sender-name">{item.sender_name}</span>
                                </div>
                                <div className="template-date">
                                  <CalendarIcon />
                                  <span>{item.created_at}</span>
                                </div>
                              </div>
                            )}
                            {currentTemplate.tab_id === 3 && (
                              <div className="template-footer">
                                <div className="template-date">
                                  <CalendarIcon />
                                  <span>{item.created_at}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-templates-message">
                        <div className="no-templates-icon">
                          <i className="bi bi-folder-x"></i>
                        </div>
                        <h3 className="no-templates-title">No Templates/Folders Available</h3>
                        <p className="no-templates-description">
                          This folder is empty.
                        </p>
                      </div>
                    )}

                  </>
                )}
              </div>

            </div>

            {/* Modals and other components remain unchanged */}
            <CreateFolder
              currentTemplate={currentTemplate}
              isRefresh={isRefresh}
              setIsRefresh={setIsRefresh}
              setLoading={setLoading}
            />
            <EditFolder
              isRefresh={isRefresh}
              setIsRefresh={setIsRefresh}
              setLoading={setLoading}
              folderData={folderData}
              setFolderData={setFolderData}
            />
            <DeleteFolder
              isRefresh={isRefresh}
              setIsRefresh={setIsRefresh}
              setLoading={setLoading}
              folderData={folderData}
              setFolderData={setFolderData}
            />
            <ShareFolder
              shareUserList={shareUserList}
              setLoading={setLoading}
              isRefresh={isRefresh}
              setIsRefresh={setIsRefresh}
              removeShareData={removeShareData}
              setRemoveShareData={setRemoveShareData}
            />
            <MoveFolder
              isRefresh={isRefresh}
              setIsRefresh={setIsRefresh}
              setLoading={setLoading}
              folderData={folderData}
              setFolderData={setFolderData}
            />
            <RenameEnvelope
              isRefresh={isRefresh}
              setIsRefresh={setIsRefresh}
              setLoading={setLoading}
              folderData={folderData}
              setFolderData={setFolderData}
            />
            <DeleteTemplateEnvelope
              isRefresh={isRefresh}
              setIsRefresh={setIsRefresh}
              setLoading={setLoading}
              folderData={folderData}
              setFolderData={setFolderData}
            />
            <MoveEnvelope
              isRefresh={isRefresh}
              setIsRefresh={setIsRefresh}
              setLoading={setLoading}
              folderData={folderData}
              setFolderData={setFolderData}
            />
            <CopyTemplateEnvelope
              isRefresh={isRefresh}
              setIsRefresh={setIsRefresh}
              setLoading={setLoading}
              folderData={folderData}
              setFolderData={setFolderData}
            />
            <DuplicateTemplate
              setLoading={setLoading}
              folderData={folderData}
              setFolderData={setFolderData}
            />
            <RemoveShareFolder
              isRefresh={isRefresh}
              setIsRefresh={setIsRefresh}
              setLoading={setLoading}
              removeShareData={removeShareData}
              setRemoveShareData={setRemoveShareData}
            />
          </main>


        </div>
      </div>
    </>
  );
}

export default Templates; 